import { KeyIcon } from '@heroicons/react/24/outline'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { PasswordStrength } from '@authy/lib-common/entities'

import { Dialog } from '@/components/Dialog'
import { TextField } from '@/components/TextField'

import { PasswordRequirements } from './PasswordRequirements'

type PasswordChangeDialogProps = {
    open: boolean
    minLength: number
    strength: PasswordStrength
    onClose: () => void
    onSubmit: (newPassword: string) => Promise<void>
}

export const PasswordChangeDialog = ({ open, minLength, strength, onClose, onSubmit }: PasswordChangeDialogProps) => {
    const { t } = useTranslation()

    const newPasswordInputRef = useRef<HTMLInputElement | null>(null)
    const [submitting, setSubmitting] = useState(false)
    const [localError, setLocalError] = useState<string | null>(null)
    const [form, setForm] = useState({
        newPassword: '',
        confirmPassword: '',
    })

    const confirmPasswordError = useMemo(() => {
        if (!form.confirmPassword.length || form.newPassword === form.confirmPassword) {
            return null
        }
        return t('password.confirmPasswordMismatch')
    }, [form.confirmPassword, form.newPassword, t])

    if (!open) {
        return null
    }

    const onChange = (field: 'newPassword' | 'confirmPassword', value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }))
        setLocalError(null)
    }

    const handleSubmit = async () => {
        if (form.newPassword !== form.confirmPassword) {
            return
        }
        setSubmitting(true)
        try {
            await onSubmit(form.newPassword)
            setForm({ newPassword: '', confirmPassword: '' })
        } catch (e) {
            setLocalError(e instanceof Error ? e.message : t('messages.passwordUpdateError'))
        } finally {
            setSubmitting(false)
        }
    }

    const handleClose = () => {
        setForm({ newPassword: '', confirmPassword: '' })
        setLocalError(null)
        onClose()
    }

    return (
        <Dialog
            open={open}
            title={t('password.dialogTitle')}
            onClose={handleClose}
            initialFocusRef={newPasswordInputRef}
            closeLabel={t('common.close')}
            footer={
                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="border-account-border bg-account-surface text-account-primary hover:bg-account-page rounded-lg border px-4 py-2 font-semibold transition"
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            void handleSubmit()
                        }}
                        disabled={submitting || !form.newPassword || !form.confirmPassword || !!confirmPasswordError}
                        className="bg-account-accent text-account-on-accent hover:bg-account-accent-hover rounded-lg px-6 py-2 font-bold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {t('password.update')}
                    </button>
                </div>
            }
        >
            <div className="border-account-divider bg-account-muted text-account-primary flex items-start gap-3 rounded-lg border px-4 py-3 text-sm">
                <KeyIcon className="text-account-secondary mt-0.5 h-5 w-5 shrink-0" />
                <p>{t('password.dialogDescription')}</p>
            </div>

            {localError && (
                <p role="alert" className="text-account-error text-sm">
                    {localError}
                </p>
            )}

            <div className="space-y-5">
                <TextField
                    inputRef={newPasswordInputRef}
                    label={t('password.newPassword')}
                    type="password"
                    autoComplete="new-password"
                    value={form.newPassword}
                    onChange={(value) => onChange('newPassword', value)}
                />
                <PasswordRequirements password={form.newPassword} strength={strength} minLength={minLength} />
                <TextField
                    label={t('password.confirmPassword')}
                    type="password"
                    autoComplete="new-password"
                    value={form.confirmPassword}
                    error={confirmPasswordError}
                    onChange={(value) => onChange('confirmPassword', value)}
                />
            </div>
        </Dialog>
    )
}
